import React from 'react';
import './assetCard.css';

/* ------------------------------------------------------------------ */
/*  AssetCard — 统一的素材 / 媒体卡片组件                               */
/*                                                                    */
/*  适用场景:                                                          */
/*    1. imageGen  —  生成结果图片卡片（网格 / 列表）                     */
/*    2. library   —  素材库资源卡片                                    */
/*    3. tasks     —  任务列表中的视频卡片                               */
/*    4. picker    —  弹窗中的素材缩略选择                               */
/* ------------------------------------------------------------------ */

/** 悬停时出现的操作按钮描述 */
export interface AssetCardAction {
  /** 按钮唯一标识 */
  key: string;
  /** 按钮图标（emoji / 文字 / ReactNode） */
  icon: React.ReactNode;
  /** tooltip 文案 */
  title?: string;
  /** 点击回调 */
  onClick: (e: React.MouseEvent) => void;
  /** 是否禁用 */
  disabled?: boolean;
}

/** 标签/徽章描述 */
export interface AssetCardBadge {
  /** 唯一标识 */
  key: string;
  /** 标签文案 */
  label: string;
  /** 可选：高亮变体 */
  variant?: 'default' | 'primary' | 'model';
  /** 位置：仅对 overlay-badge 有效（叠加在预览图内） */
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

export interface AssetCardProps {
  /** 预览图 URL */
  src?: string | null;
  /** 缩略图 URL（优先用于展示，src 作为全尺寸备选） */
  thumb?: string | null;
  /** alt 文案（可访问性） */
  alt?: string;

  /* ---------- 信息区 ---------- */
  /** 卡片标题（可选，显示在预览区下方） */
  title?: string;
  /** 副标题 / 描述 */
  subtitle?: string;
  /** 额外信息（时间、尺寸等）*/
  meta?: string;

  /* ---------- 交互 ---------- */
  /** 悬停时在预览区右上角显示的操作按钮 */
  overlayActions?: AssetCardAction[];
  /** 居中覆盖层按钮（如"使用素材"） */
  centerAction?: {
    label: string;
    onClick: (e: React.MouseEvent) => void;
  };
  /** 多个居中按钮（与 centerAction 二选一，优先此项） */
  centerActions?: Array<{
    label: string;
    onClick: (e: React.MouseEvent) => void;
  }>;
  /** 右上角删除按钮 */
  deleteAction?: {
    onClick: (e: React.MouseEvent) => void;
    busy?: boolean;
    disabled?: boolean;
  };
  /** 底部操作按钮组 */
  footerActions?: AssetCardAction[];

  /* ---------- 装饰 ---------- */
  /** 叠加在预览图上的徽章（如模型名称、AI生成水印等） */
  overlayBadges?: AssetCardBadge[];
  /** 预览图下方的标签列表 */
  tags?: AssetCardBadge[];

  /* ---------- 状态 ---------- */
  /** 空态文案 */
  emptyText?: string;

  /* ---------- 布局 ---------- */
  /** 卡片变体 */
  variant?: 'card' | 'compact' | 'mini';
  /** 整个卡片的 onClick */
  onClick?: (e: React.MouseEvent) => void;
  /** 额外 className */
  className?: string;
  /** 额外 style */
  style?: React.CSSProperties;

  /** 子元素渲染在信息区下方（用于自定义内容） */
  children?: React.ReactNode;
}

/** 通用素材卡片组件 - 专注于图片展示 */
export const AssetCard: React.FC<AssetCardProps> = ({
  src,
  thumb,
  alt = '',
  title,
  subtitle,
  meta,
  overlayActions,
  centerAction,
  centerActions,
  deleteAction,
  footerActions,
  overlayBadges,
  tags,
  emptyText,
  variant = 'card',
  onClick,
  className,
  style,
  children,
}) => {
  const previewSrc = thumb || src || '';
  const hasPreview = !!previewSrc;
  const resolvedCenterActions =
    centerActions && centerActions.length > 0
      ? centerActions
      : centerAction
        ? [centerAction]
        : [];
  const rootCls = [
    'ac-root',
    `ac-root--${variant}`,
    onClick ? 'ac-root--clickable' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootCls} style={style} onClick={onClick}>
      {/* ====== 预览区 ====== */}
      <div className="ac-preview">
        {hasPreview ? (
          <img
            className="ac-media"
            src={previewSrc}
            alt={alt}
            referrerPolicy="no-referrer"
            loading="lazy"
          />
        ) : (
          <div className="ac-empty-preview">{emptyText || '无内容'}</div>
        )}

        {/* 悬停操作按钮（右上角） */}
        {overlayActions && overlayActions.length > 0 && (
          <div className="ac-overlay-actions">
            {overlayActions.map((action) => (
              <button
                key={action.key}
                type="button"
                className="ac-overlay-btn"
                title={action.title}
                disabled={action.disabled}
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}

        {/* 居中操作按钮 */}
        {resolvedCenterActions.length > 0 && (
          <div className="ac-center-overlay">
            {resolvedCenterActions.map((action, idx) => (
              <button
                key={`${action.label}-${idx}`}
                type="button"
                className="ac-center-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick(e);
                }}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* 删除按钮 */}
        {deleteAction && (
          <button
            type="button"
            className="ac-delete-btn"
            disabled={deleteAction.disabled || deleteAction.busy}
            onClick={(e) => {
              e.stopPropagation();
              deleteAction.onClick(e);
            }}
          >
            {deleteAction.busy ? '⏳' : '✕'}
          </button>
        )}

        {/* Overlay 徽章（叠加在预览图内） */}
        {overlayBadges?.map((badge) => (
          <span
            key={badge.key}
            className={`ac-overlay-badge ac-overlay-badge--${badge.position || 'top-left'}`}
          >
            {badge.label}
          </span>
        ))}
      </div>

      {/* ====== 信息区 ====== */}
      {(title || subtitle || meta || tags || footerActions || children) && (
        <div className="ac-info">
          {title && <div className="ac-title">{title}</div>}
          {subtitle && <div className="ac-subtitle">{subtitle}</div>}
          {meta && <div className="ac-meta">{meta}</div>}

          {/* 标签列表 */}
          {tags && tags.length > 0 && (
            <div className="ac-tags">
              {tags.map((tag) => (
                <span
                  key={tag.key}
                  className={`ac-tag${tag.variant === 'primary' || tag.variant === 'model' ? ` ac-tag--${tag.variant}` : ''}`}
                >
                  {tag.label}
                </span>
              ))}
            </div>
          )}

          {/* 自定义子内容 */}
          {children}

          {/* 底部操作按钮 */}
          {footerActions && footerActions.length > 0 && (
            <div className="ac-footer-actions">
              {footerActions.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  className="ac-footer-btn"
                  title={action.title}
                  disabled={action.disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    action.onClick(e);
                  }}
                >
                  {action.icon}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetCard;
